import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, GitPullRequestArrow, PlayCircle, SearchCheck, XCircle } from 'lucide-react';
import { AI_ENGINEERING_REJECTABLE_STATUSES, getAIEngineeringStatusLabel, getAIEngineeringStatusTone } from '@/lib/aiEngineeringLabels';

export const AIEngineeringApprovalBar = ({ task, actionBusy, permissionMatrix, onAnalyze, onGeneratePatch, onRunTests, onApprove, onReject, onOpenPr }) => {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const status = task?.status || '';
  const canAnalyzePermission = Boolean(permissionMatrix?.['ai_engineering.analyze']?.allowed);
  const canSuggestFixPermission = Boolean(permissionMatrix?.['ai_engineering.suggest_fix']?.allowed);
  const canApprovePermission = Boolean(permissionMatrix?.['ai_engineering.approve']?.allowed);

  const controls = useMemo(() => ({
    canAnalyze: status === 'draft' && canAnalyzePermission,
    canGeneratePatch: status === 'analyzed' && canSuggestFixPermission,
    canRunTests: status === 'patch_generated' && canSuggestFixPermission,
    canApprove: status === 'tests_passed' && canApprovePermission,
    canReject: AI_ENGINEERING_REJECTABLE_STATUSES.includes(status) && canApprovePermission,
    canOpenPr: status === 'approved' && canApprovePermission,
  }), [canAnalyzePermission, canApprovePermission, canSuggestFixPermission, status]);

  const submitReject = async () => {
    const cleanReason = rejectReason.trim();
    if (!cleanReason) return;
    await onReject(cleanReason);
    setRejectReason('');
    setRejectOpen(false);
  };

  return (
    <>
      <div className="sticky bottom-0 z-10 rounded-sm border border-slate-300 bg-white/95 p-3 backdrop-blur" data-testid="ai-engineering-approval-bar">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={`rounded-sm border ${getAIEngineeringStatusTone(status || 'draft')}`}>{getAIEngineeringStatusLabel(status || 'draft')}</Badge>
            <span className="text-xs text-slate-500">Analyze Real e Suggest Fix Real usam provider real quando configurados; Apply to Sandbox continua desactivado e não existe escrita real no repositório.</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="h-8 rounded-sm px-3 text-xs" onClick={onAnalyze} disabled={!task || !controls.canAnalyze || actionBusy} data-testid="ai-engineering-analyze-button"><SearchCheck className="mr-2 h-3.5 w-3.5" /> Analyze</Button>
            <Button variant="outline" size="sm" className="h-8 rounded-sm px-3 text-xs" onClick={onGeneratePatch} disabled={!task || !controls.canGeneratePatch || actionBusy} data-testid="ai-engineering-generate-patch-button">Suggest Fix</Button>
            <Button variant="outline" size="sm" className="h-8 rounded-sm px-3 text-xs" onClick={onRunTests} disabled={!task || !controls.canRunTests || actionBusy} data-testid="ai-engineering-run-tests-button"><PlayCircle className="mr-2 h-3.5 w-3.5" /> Run Tests</Button>
            <Button size="sm" className="h-8 rounded-sm bg-slate-900 px-3 text-xs hover:bg-slate-800" onClick={onApprove} disabled={!task || !controls.canApprove || actionBusy} data-testid="ai-engineering-approve-button"><CheckCircle2 className="mr-2 h-3.5 w-3.5" /> Approve</Button>
            <Button variant="outline" size="sm" className="h-8 rounded-sm border-red-200 px-3 text-xs text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => setRejectOpen(true)} disabled={!task || !controls.canReject || actionBusy} data-testid="ai-engineering-reject-button"><XCircle className="mr-2 h-3.5 w-3.5" /> Reject</Button>
            <Button variant="outline" size="sm" className="h-8 rounded-sm px-3 text-xs" onClick={onOpenPr} disabled={!task || !controls.canOpenPr || actionBusy} data-testid="ai-engineering-open-pr-button"><GitPullRequestArrow className="mr-2 h-3.5 w-3.5" /> Open PR</Button>
          </div>
        </div>
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-lg rounded-sm border-slate-300">
          <DialogHeader>
            <DialogTitle>Rejeitar task</DialogTitle>
            <DialogDescription>O motivo é obrigatório e será persistido no histórico da task.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="ai-engineering-reject-reason" className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Motivo</Label>
            <Textarea id="ai-engineering-reject-reason" value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} className="min-h-[120px] resize-none" placeholder="Descreva claramente porque o pedido deve ser rejeitado nesta fase." data-testid="ai-engineering-reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={submitReject} disabled={!rejectReason.trim() || actionBusy} data-testid="ai-engineering-reject-submit-button">Confirmar rejeição</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};